/**
 * Gate Pass APIs — material entering or leaving hospital premises.
 * Uses inventory axios client — ORG-CODE resolves tenant/hospital server-side.
 *
 * NO ENDPOINT HERE MOVES STOCK. A gate pass is a movement record and a printable
 * document — gate-out, gate-in and close-not-returned all leave stock_balances
 * and the ledger untouched, for returnable and non-returnable alike. The document
 * that disposes of the material (Vendor Return, Goods Discard, Stock Transfer,
 * Stock Adjustment) is what posts.
 *
 * ACTOR IDENTITY TRAVELS IN THE BODY, exactly as in every other inventory api
 * file. created_by / approved_by / rejected_by / gate_out_by / gate_in_by /
 * closed_by are attached here from the signed-in session, and the EMR wrapper
 * forwards the body untouched — see the "Gate Pass" comment block above
 * InventoryGatePassView in manage_inventory_stock.py, which says explicitly:
 * do NOT stamp these from the JWT, because stock-ledger-service types the
 * columns as uuid while the JWT user_id is a Django integer pk.
 *
 * The *_by_name variants are gate-pass-specific: gate_pass_approvals snapshots
 * actor_name, and that snapshot is what the approval trail and the printed
 * challan display. Without it the trail reads "STORE · APPROVED" with no name.
 */
import client from './inventory-request';
import {
  withStoreCreateBody,
  getStoreActorId,
  getStoreActorName,
} from '../hooks/useStoreScope';

/** The signed-in user, in the shape every gate-pass action endpoint expects. */
function actor(idKey, nameKey, opts) {
  return {
    [idKey]: opts[idKey] || getStoreActorId() || undefined,
    [nameKey]: opts[nameKey] || getStoreActorName() || undefined,
  };
}

// API: POST /inventory/gate-pass/
export const createGatePass = (body) =>
  client.post('/inventory/gate-pass/', withStoreCreateBody(body));

// API: GET /inventory/gate-pass/
export const getGatePasses = (p) =>
  client.get('/inventory/gate-pass/', p);

// API: GET /inventory/gate-pass/outstanding/  → returnable, out, not yet back
export const getOutstandingGatePasses = (p) =>
  client.get('/inventory/gate-pass/outstanding/', p);

// API: GET /inventory/gate-pass/pending-approval/?stage=STORE|SECURITY
export const getPendingApprovals = (p) =>
  client.get('/inventory/gate-pass/pending-approval/', p);

// API: GET /inventory/gate-pass/{id}/
export const getGatePass = (id) =>
  client.get(`/inventory/gate-pass/${id}/`);

/**
 * API: PUT /inventory/gate-pass/{id}/   (DRAFT only)
 *
 * `direction` and `returnable_flag` are NOT accepted by GatePassUpdate — they decide
 * whether the document owes a return at all, so flipping them on an existing
 * number would silently change what the document means. Strip them before sending.
 */
export const updateGatePass = (id, body) => {
  const { direction, returnable_flag: _returnable, ...editable } = body || {};
  return client.put(`/inventory/gate-pass/${id}/`, editable);
};

// API: DELETE /inventory/gate-pass/{id}/  → CANCELLED
export const cancelGatePass = (id) =>
  client.delete(`/inventory/gate-pass/${id}/`);

// API: POST /inventory/gate-pass/{id}/submit/
export const submitGatePass = (id) =>
  client.post(`/inventory/gate-pass/${id}/submit/`, {});

/**
 * API: POST /inventory/gate-pass/{id}/approve/
 *
 * `stage` is mandatory: the EMR wrapper picks which permission to enforce from it,
 * without a round-trip to read the current status. The service rejects a stage that
 * does not match the pass's actual state, so it is a routing hint, never a trust
 * boundary — pass what pendingStageOf() returned.
 */
export const approveGatePass = (id, opts = {}) =>
  client.post(`/inventory/gate-pass/${id}/approve/`, {
    stage: opts.stage,
    ...actor('approved_by', 'approved_by_name', opts),
    remarks: opts.remarks || undefined,
  });

// API: POST /inventory/gate-pass/{id}/reject/   (rejection_reason is required)
export const rejectGatePass = (id, opts = {}) =>
  client.post(`/inventory/gate-pass/${id}/reject/`, {
    stage: opts.stage,
    ...actor('rejected_by', 'rejected_by_name', opts),
    rejection_reason: opts.rejection_reason || opts.reason,
  });

/**
 * API: POST /inventory/gate-pass/{id}/gate-out/  → material leaves. No stock effect.
 *
 * `gate_out_at` is the moment the material actually crossed the gate, as recorded
 * by whoever is at the barrier — not the moment this request is made. Omitted, the
 * service falls back to its own now(); in the future it is rejected.
 */
export const gateOutGatePass = (id, opts = {}) =>
  client.post(`/inventory/gate-pass/${id}/gate-out/`, {
    gate_out_at: opts.gate_out_at || undefined,
    ...actor('gate_out_by', 'gate_out_by_name', opts),
    vehicle_no: opts.vehicle_no || undefined,
    carrier_name: opts.carrier_name || undefined,
    carrier_contact: opts.carrier_contact || undefined,
    remarks: opts.remarks || undefined,
  });

/**
 * API: POST /inventory/gate-pass/{id}/gate-in/
 *
 * Credits returned_quantity on the linked outward pass, or simply records the
 * arrival on a standalone one. No stock effect either way.
 * GatePassGateInRequest takes remarks only — the carrier details already sit on the
 * inward document itself, so do not send vehicle/carrier here; they are dropped.
 */
export const gateInGatePass = (id, opts = {}) =>
  client.post(`/inventory/gate-pass/${id}/gate-in/`, {
    ...actor('gate_in_by', 'gate_in_by_name', opts),
    remarks: opts.remarks || undefined,
  });

// API: POST /inventory/gate-pass/{id}/close-not-returned/  (reason is required)
export const closeNotReturned = (id, opts = {}) =>
  client.post(`/inventory/gate-pass/${id}/close-not-returned/`, {
    ...actor('closed_by', 'closed_by_name', opts),
    reason: opts.reason,
  });
